/* eslint-disable i18next/no-literal-string -- TanStack cache key segments */
import type { JobAssetKind } from './job-assets';

export const MEDIA_CAMPAIGN_QUERY_KEYS = {
  all: ['media-campaigns'] as const,
  connectorAccounts: () => [...MEDIA_CAMPAIGN_QUERY_KEYS.all, 'connector-accounts'] as const,
  jobs: (projectId: string) => [...MEDIA_CAMPAIGN_QUERY_KEYS.all, 'jobs', projectId] as const,
  /**
   * Every project's job list, for changes made from inside a job that the list also
   * shows — the platforms on a job, say, which the list renders as icons. The job screen
   * knows its job id and not its project id, so it cannot name the one list to refresh.
   */
  allJobs: () => [...MEDIA_CAMPAIGN_QUERY_KEYS.all, 'jobs'] as const,
  job: (projectId: string, jobId: string) =>
    [...MEDIA_CAMPAIGN_QUERY_KEYS.all, 'job', projectId, jobId] as const,
  jobPlan: (jobId: string) => [...MEDIA_CAMPAIGN_QUERY_KEYS.all, 'job-plan', jobId] as const,
  jobAsset: (jobId: string, fileName: string, kind?: JobAssetKind) =>
    [...MEDIA_CAMPAIGN_QUERY_KEYS.all, 'job-asset', jobId, fileName.toLowerCase(), kind] as const,
  jobHistory: (jobId: string) => [...MEDIA_CAMPAIGN_QUERY_KEYS.all, 'job-history', jobId] as const,
  platformSchemas: () => [...MEDIA_CAMPAIGN_QUERY_KEYS.all, 'platform-schemas'] as const,
  connectorPages: (connectorId: string) =>
    [...MEDIA_CAMPAIGN_QUERY_KEYS.all, 'connector-pages', connectorId] as const,
  connectorAdvertisers: (connectorId: string, search: string) =>
    [...MEDIA_CAMPAIGN_QUERY_KEYS.all, 'connector-advertisers', connectorId, search] as const,
  /**
   * Keyed on the whole window, dimension, and campaign scope: any of the three is a
   * different question, not a stale answer, so each must refetch rather than reuse
   * another one's numbers. Without the scope in the key, a campaign-scoped view would
   * read the project-wide entry from cache and show the wrong campaign's numbers with
   * no request and no way to notice.
   *
   * `platforms`/`channels` are the caller's already order-independent comma-joined
   * strings (see `commaJoined` in `performance-queries.ts`), so `dv360,meta_ads` and
   * `meta_ads,dv360` never produce two cache entries for what is one selection.
   */
  performance: (
    projectId: string,
    startDate: string,
    endDate: string,
    dimension: string,
    campaignId?: string,
    platforms?: string,
    channels?: string,
  ) =>
    [
      ...MEDIA_CAMPAIGN_QUERY_KEYS.performanceAll(projectId),
      startDate,
      endDate,
      dimension,
      campaignId,
      platforms,
      channels,
    ] as const,
  /** Prefix shared by every window+dimension for a project, for invalidating all of them at once. */
  performanceAll: (projectId: string) =>
    [...MEDIA_CAMPAIGN_QUERY_KEYS.all, 'performance', projectId] as const,
  campaigns: (projectId: string) =>
    [...MEDIA_CAMPAIGN_QUERY_KEYS.all, 'campaigns', projectId] as const,
  discoveredCampaigns: (projectId: string) =>
    [...MEDIA_CAMPAIGN_QUERY_KEYS.all, 'discovered-campaigns', projectId] as const,
  connectorCampaigns: (connectorId: string) =>
    [...MEDIA_CAMPAIGN_QUERY_KEYS.all, 'connector-campaigns', connectorId] as const,
} as const;
